<script>
  import { fade } from 'svelte/transition';

  let { open = false, onClose, title = '', children, footer } = $props();

  let bodyEl = $state(null);
  let dragY = $state(0);       // px the sheet is dragged down from its resting spot
  let dragging = $state(false);
  let closing = $state(false); // animating out (drag-release or explicit close)
  let entered = $state(false); // false on mount so the sheet starts off-screen, then slides up
  let startY = 0;
  let eligible = false;        // this gesture may turn into a drag-to-close

  // Reset the per-open animation state and lock the background scroll while open.
  $effect(() => {
    if (!open) return;
    closing = false;
    dragging = false;
    dragY = 0;
    entered = false;
    requestAnimationFrame(() => requestAnimationFrame(() => { entered = true; }));

    // Lock the background scroll with overflow only. `touch-action: none` on
    // <body> must NOT be used here: engines apply the ancestor restriction to
    // nested scrollers too, which made the sheet body unscrollable by touch.
    // The scrim carries its own touch-action lock and .bs-body contains its
    // overscroll instead (see styles below).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('overlay-open'); // hides the floating notification bell
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('overlay-open');
    };
  });

  // Slide fully down, then unmount (all closes route through here so it always animates).
  function animateClose() {
    if (closing) return;
    closing = true;
    dragging = false;
    dragY = typeof window !== 'undefined' ? window.innerHeight : 800;
    setTimeout(() => onClose?.(), 240);
  }

  function handleKeydown(e) {
    if (open && e.key === 'Escape') animateClose();
  }

  function onPointerDown(e) {
    if (closing) return;
    // A drag-to-close may begin from the top handle, or anywhere when the body is
    // already scrolled to the top (so normal content scrolling still works below).
    const inHandle = e.target.closest?.('.bs-drag');
    // Never hijack taps on the actual controls (✕, buy button, links, selects) —
    // outside the handle those must click normally. (This is why the ✕ and price
    // button previously did nothing: pointer capture swallowed their click.)
    if (!inHandle && e.target.closest?.('button, a, select, input, textarea, label')) {
      eligible = false;
      return;
    }
    const bodyAtTop = !bodyEl || bodyEl.scrollTop <= 0;
    eligible = !!inHandle || bodyAtTop;
    if (!eligible) return;
    startY = e.clientY;
    dragging = false;
    // NOTE: capture only once a real drag starts (in onPointerMove), so a plain
    // tap still reaches the button/handle onclick.
  }

  function onPointerMove(e) {
    if (!eligible || closing) return;
    const delta = e.clientY - startY;
    if (dragging) {
      // Clamp at the resting position — dragging back above the start point
      // must not lift the sheet and expose a gap beneath it.
      dragY = Math.max(0, delta);
      if (e.cancelable) e.preventDefault();
      return;
    }
    if (delta > 4) {
      dragging = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragY = delta;
      if (e.cancelable) e.preventDefault(); // stop the body from scrolling mid-drag
    } else if (delta < -4 && !dragging) {
      eligible = false; // moved up first → it's a scroll, not a close
    }
  }

  function onPointerUp(e) {
    if (!eligible) return;
    eligible = false;
    if (!dragging) { dragY = 0; return; }
    dragging = false;
    const nearBottom = e.clientY > window.innerHeight - 90;
    if (nearBottom || dragY > 120) {
      animateClose();       // committed → slide out and close
    } else {
      dragY = 0;            // not far enough → snap back (CSS transition animates it)
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

{#if open}
  <div class="bs-root" role="dialog" aria-modal="true" aria-label={title || 'Details'}>
    <div
      class="bs-scrim"
      transition:fade={{ duration: 180 }}
      onclick={animateClose}
      role="presentation"
    ></div>
    <!-- Drag-to-close is a progressive enhancement; the ✕ button, scrim tap and
         Escape provide accessible close paths, so static pointer handlers are fine. -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="bs-sheet"
      class:dragging
      style="transform: translateY({closing || dragging ? dragY + 'px' : (entered ? '0px' : '100%')});"
      onpointerdown={onPointerDown}
      onpointermove={onPointerMove}
      onpointerup={onPointerUp}
      onpointercancel={onPointerUp}
    >
      <div class="bs-drag">
        <button class="bs-handle-hit" onclick={animateClose} aria-label="Close">
          <span class="bs-handle"></span>
        </button>
      </div>
      <button class="bs-close" onclick={animateClose} aria-label="Close">✕</button>
      <div class="bs-body" bind:this={bodyEl}>
        {@render children?.()}
      </div>
      {#if footer}
        <div class="bs-footer">
          {@render footer()}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
  @use '../styles/breakpoints.scss' as bp;
  @use '../styles/scrollbar.scss' as sb;

  .bs-root {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .bs-scrim {
    position: absolute;
    inset: 0;
    background: rgba(8, 4, 1, 0.62);
    backdrop-filter: blur(1px);
    touch-action: none; // pans on the backdrop must not scroll the page behind
  }

  .bs-sheet {
    position: relative;
    z-index: 1;
    background: #2a190d;
    border-top: 1px solid #5a3a1c;
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.5);
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    will-change: transform;
    transition: transform 0.26s ease;

    &.dragging {
      transition: none; // follow the finger 1:1 while dragging
    }
  }

  // Top grab zone — a drag-down here (or anywhere once the body is scrolled to top)
  // pulls the sheet down to close. touch-action:none so the browser doesn't claim
  // the gesture as a page scroll.
  .bs-drag {
    flex-shrink: 0;
    touch-action: none;
  }

  .bs-handle-hit {
    width: 100%;
    padding: 10px 0 6px;
    border: none;
    background: transparent;
    cursor: grab;
    display: flex;
    justify-content: center;
  }

  .bs-handle {
    width: 44px;
    height: 5px;
    border-radius: 3px;
    background: #8a6231;
  }

  // Explicit close affordance — more opaque/solid than before so it reads clearly
  // over any pack art.
  .bs-close {
    position: absolute;
    top: 10px;
    right: 12px;
    z-index: 3;
    width: 38px;
    height: 38px;
    border-radius: 999px;
    border: 2px solid rgba(255, 255, 255, 0.7);
    background: rgba(0, 0, 0, 0.82);
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    -webkit-tap-highlight-color: transparent;
  }

  .bs-body {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    // Keep overscroll from chaining to the page (no pull-to-refresh behind the
    // sheet); the drag-to-close JS still sees the gesture via pointer events.
    overscroll-behavior: contain;
    padding: 4px 16px 16px;
    margin-right: 2px;
    @include sb.custom-scrollbar;
  }

  .bs-footer {
    flex-shrink: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding: 12px 16px calc(12px + #{bp.$safe-bottom});
    background: rgba(0, 0, 0, 0.25);
  }
</style>
